import axios, { AxiosRequestConfig } from 'axios';
import {
	DocumentModel,
	DocumentPostPayload,
	MergeDocumentPayload,
} from '../data/interfaces/Document';
import {
	DocumentTemplate,
	DocumentTemplateEditUrlResponse,
} from '../data/interfaces/DocumentTemplate';
import { getLogger } from '../logger';
import { PatchPayload } from './interfaces/APIRequest';
import {
	CopyFileFromSharefilePayload,
	DownloadFileResponse,
	GetThumbnailResponse,
} from './interfaces/DocumentTemplateFile';
import { getCachedToken, setCachedToken } from './tokenCache';

interface Configuration {
	baseUrl?: string;
	useBffAuthProxy?: boolean;
	getAccessToken?: () => Promise<string | undefined>;
}

export const configuration: Configuration = {};

export function configureDocgenClient(config: Configuration) {
	Object.assign(configuration, config);
}

async function apiProxy<ResponseType>(
	requestConfig: AxiosRequestConfig,
	requestHeaders = {},
	isRetry = false
): Promise<ResponseType> {
	if (requestConfig.method === 'PATCH') {
		requestHeaders = {
			'Content-Type': 'application/json-patch+json',
		};
	}

	if (configuration.useBffAuthProxy) {
		requestConfig['headers'] = { 'X-BFF-CSRF': 'true', ...requestHeaders };
	} else {
		const token = await getToken({ useCache: true });

		requestConfig['headers'] = {
			Authorization: `Bearer ${token}`,
			...requestHeaders,
		};
	}

	getLogger().logInfo('API request config', {
		requestConfig: JSON.stringify(requestConfig),
	});

	const response = await axios.request<ResponseType>(requestConfig);
	if (response.status === 401 && !configuration.useBffAuthProxy && !isRetry) {
		await getToken({ useCache: false });
		return apiProxy<ResponseType>(requestConfig, true);
	} else if (response.status < 200 || response.status > 399) {
		getLogger().logError(new Error(JSON.stringify(response)), {
			customMessage: 'Error in API response',
		});
		throw new Error(JSON.stringify(response));
	}
	return response.data;
}

async function getToken({ useCache }: { useCache?: boolean }) {
	if (useCache) {
		const cachedToken = getCachedToken();
		if (cachedToken) {
			return cachedToken;
		}
	}

	const freshToken = await configuration?.getAccessToken?.();
	setCachedToken(freshToken!);
	return freshToken;
}

export const DocgenClient = {
	async saveSignature(payload: any) {
		const endpoint = `${configuration.baseUrl}/v1/signatures`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: payload,
		};

		return apiProxy(requestConfig);
	},
	async getTemplateByRID<T>(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<T>(requestConfig);
	},
	async createTemplate<T>(templateType) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: { templateType },
		};

		return apiProxy<T>(requestConfig);
	},
	async createFileUpload(payload: FormData, templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}/file/upload`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: payload,
		};

		return apiProxy(requestConfig);
	},

	async copyFileFromSharefile(
		templateRID: string,
		payload: CopyFileFromSharefilePayload
	) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}/file`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: payload,
		};

		return apiProxy(requestConfig);
	},

	async deleteUploadedFile(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}/file`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'DELETE',
		};

		return apiProxy(requestConfig);
	},

	async getEditUrlForRID(documentTemplateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			documentTemplateRID
		)}/file/editUrl`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<DocumentTemplateEditUrlResponse>(requestConfig);
	},

	async getFileThumbnail(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}/file/thumbnail`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<GetThumbnailResponse>(requestConfig);
	},

	async downloadFile(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}/file/download`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<DownloadFileResponse>(requestConfig);
	},

	async updateDocumentTemplate(payload: PatchPayload, templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'PATCH',
			data: payload,
		};

		return apiProxy<DocumentTemplate>(requestConfig);
	},
	async getTemplates(payload?: { searchTerm?: string; continuationToken?: string }) {
		const { searchTerm, continuationToken } = payload || {};

		let endpoint = `${configuration.baseUrl}/v1/documentTemplates`;

		if (searchTerm) {
			endpoint += `?search=${encodeURIComponent(searchTerm)}`;
		}

		if (continuationToken) {
			const separator = searchTerm ? '&' : '?';
			endpoint += `${separator}continuationToken=${encodeURIComponent(
				continuationToken
			)}`;
		}

		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<any>(requestConfig);
	},

	async createDocument(payload: DocumentPostPayload) {
		const endpoint = `${configuration.baseUrl}/v1/documents`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: payload,
		};

		return apiProxy<DocumentModel>(requestConfig);
	},
	async getDocumentbyRID(documentRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documents/${encodeURIComponent(
			documentRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<any>(requestConfig);
	},
	async downloadDocumentFile(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documents/${encodeURIComponent(
			templateRID
		)}/file/download`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<DownloadFileResponse>(requestConfig);
	},
	async updateDocument(payload: PatchPayload, documentRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documents/${encodeURIComponent(
			documentRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'PATCH',
			data: payload,
		};

		return apiProxy<DocumentModel>(requestConfig);
	},
	async getDataSources() {
		const endpoint = `${configuration.baseUrl}/v1/dataSources`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<any>(requestConfig);
	},
	async getEntitiesByDataSource(dataSource: string) {
		const endpoint = `${configuration.baseUrl}/v1/dataSources/${encodeURIComponent(
			dataSource
		)}/entities`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<any>(requestConfig);
	},
	async getSchema(dataSource: string, entityId: string) {
		const endpoint = `${configuration.baseUrl}/v1/dataSources/${encodeURIComponent(
			dataSource
		)}/entities/${encodeURIComponent(entityId)}/schema`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'GET',
		};

		return apiProxy<any>(requestConfig);
	},
	async mergeDocument(documentRID: string, payload?: MergeDocumentPayload) {
		const endpoint = `${configuration.baseUrl}/v1/documents/${encodeURIComponent(
			documentRID
		)}/merge`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'POST',
			data: payload,
		};

		return apiProxy<DocumentModel>(requestConfig);
	},
	async deleteTemplate(templateRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documentTemplates/${encodeURIComponent(
			templateRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'DELETE',
		};

		return apiProxy(requestConfig);
	},
	async deleteDocument(documentRID: string) {
		const endpoint = `${configuration.baseUrl}/v1/documents/${encodeURIComponent(
			documentRID
		)}`;
		const requestConfig: AxiosRequestConfig = {
			url: endpoint,
			method: 'DELETE',
		};

		return apiProxy(requestConfig);
	},
};
